import type {
  BnnReportResponse,
  BusinessPartnerCompatRequest,
  BusinessPartnerCompatResponse,
  BusinessYogaResponse,
  CareerCompatibilityResponse,
  CareerV2Response,
  ChartCreateRequest,
  ChartResponse,
  CompatibilityRequest,
  CompatibilityResponse,
  HealthResponse,
  HealthVulnerabilityResponse,
  I18nBundle,
  KarmicPathResponse,
  Lang,
  LegalYogaResponse,
  ManglikResponse,
  PitraDoshaResponse,
  ProgenyYogaResponse,
  RectificationRequest,
  RectificationResponse,
  SadeSatiResponse,
  ShadbalaFullResponse,
  VarshaphalResponse,
  VidyaYogaResponse,
  VisaYogaResponse,
  WealthYogaResponse,
} from './types';

const API_BASE = '/api/v1';

class ApiError extends Error {
  status: number;
  detail: string;
  requestId?: string;
  constructor(status: number, detail: string, requestId?: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.requestId = requestId;
  }
}

async function rawRequest(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(`${API_BASE}${path}`, {
    credentials: 'same-origin',  // send cookies for auth
    ...init,
    headers,
  });
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await rawRequest(path, init);
  const requestId = res.headers.get('x-request-id') ?? undefined;
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const errJson = await res.json();
      detail = errJson?.detail ?? JSON.stringify(errJson);
    } catch {
      // ignore JSON parse failure on error body
    }
    throw new ApiError(res.status, detail, requestId);
  }
  return (await res.json()) as T;
}

interface WhoAmI {
  username: string;
  role: 'professor' | 'student';
  sv: number;
}

interface LoginOk {
  ok: true;
  user: WhoAmI;
}

interface NarrationResponse {
  chart_id: number;
  narrative_hi: string;
  narrative_en: string;
  provider: string;
  cached: boolean;
  generated_at: string;
}

interface GeocodeOut {
  display_name: string;
  lat: number;
  lon: number;
  country: string | null;
  country_code: string | null;
}

export const api = {
  // Health & i18n
  health: () => request<HealthResponse>('/healthz'),
  i18n: (lang: Lang) => request<I18nBundle>(`/i18n/${lang}`),

  // Auth
  login: (username: string, password: string) =>
    request<LoginOk>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () =>
    request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  logoutAll: () =>
    request<{ ok: true; new_session_version: number }>(
      '/auth/logout-all', { method: 'POST' },
    ),
  me: () => request<WhoAmI>('/auth/me'),

  // Geocoding
  geocode: (q: string) =>
    request<GeocodeOut>(`/geocode?q=${encodeURIComponent(q)}`),
  geocodeSearch: (q: string, limit = 5) =>
    request<GeocodeOut[]>(
      `/geocode/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),

  // Charts
  sampleChart: (asOf?: string) =>
    request<ChartResponse>(`/charts/sample${asOf ? `?as_of=${asOf}` : ''}`),
  createChart: (body: ChartCreateRequest, asOf?: string) =>
    request<ChartResponse>(`/charts${asOf ? `?as_of=${asOf}` : ''}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Career v2 — fully deterministic structured-finding report.
  // Replaces the v1 LLM-narrative-centric career report.
  careerV2: (body: ChartCreateRequest, asOf?: string) =>
    request<CareerV2Response>(
      `/charts/career-v2${asOf ? `?as_of=${asOf}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Janma Kundli — free classical-chart PDF (positions, dashas,
  // ashtakavarga, shadbala, divisional charts). The `view` toggle
  // matches the public endpoint's query param.
  janmaKundliPdfBlob: async (
    body: ChartCreateRequest,
    lang: Lang = 'hi',
    view: 'standard' | 'pro' = 'standard',
  ): Promise<Blob> => {
    const qs = new URLSearchParams({ lang, view });
    const res = await rawRequest(`/reports/janma-kundli.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // Career v2 PDF — same input, returns a Blob.
  careerV2PdfBlob: async (body: ChartCreateRequest, lang: Lang = 'hi', asOf?: string): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    if (asOf) qs.set('as_of', asOf);
    const res = await rawRequest(`/reports/career-v2.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // Business Partner Compatibility — six-axis deterministic report.
  businessPartnerCompat: (body: BusinessPartnerCompatRequest, asOf?: string) =>
    request<BusinessPartnerCompatResponse>(
      `/charts/business-partner-compat${asOf ? `?as_of=${asOf}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  businessPartnerCompatPdfBlob: async (
    body: BusinessPartnerCompatRequest,
    lang: Lang = 'hi',
    asOf?: string,
  ): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    if (asOf) qs.set('as_of', asOf);
    const res = await rawRequest(`/reports/business-partner-compat.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // Complete Astrological Profile — flagship composite report.
  // Returns a large JSON bundling every v2 chapter for one chart.
  completeProfile: (body: ChartCreateRequest, asOf?: string) =>
    request<unknown>(
      `/charts/complete-profile${asOf ? `?as_of=${asOf}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  completeProfilePdfBlob: async (
    body: ChartCreateRequest,
    lang: Lang = 'hi',
    asOf?: string,
  ): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    if (asOf) qs.set('as_of', asOf);
    const res = await rawRequest(`/reports/complete-profile.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },
  careerReport: (body: ChartCreateRequest, asOf?: string) =>
    request<ChartResponse>(`/reports/career${asOf ? `?as_of=${asOf}` : ''}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // LLM narration — Phase 5.7. Cached on the SavedChart row;
  // ?force=true regenerates. The route is unauthenticated (n8n calls
  // it from outside the LAN).
  narrate: (chartId: number, force = false) =>
    request<NarrationResponse>(
      `/narrate/${chartId}${force ? '?force=true' : ''}`,
    ),

  // PDF — returns a Blob, not JSON
  careerPdfBlob: async (body: ChartCreateRequest, asOf?: string, lang: Lang = 'hi'): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    if (asOf) qs.set('as_of', asOf);
    const res = await rawRequest(`/reports/career.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // BNN — independent simple report. Saturn-house karma + conjunctions
  // + Bhrigu Bindu + Jupiter direction. No yogas / Shadbala / Ashtakavarga.
  bnnReport: (body: ChartCreateRequest) =>
    request<BnnReportResponse>('/bnn', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  bnnSample: () => request<BnnReportResponse>('/bnn/sample'),

  // Karmic Path Analysis — narrative-style 11-section report.
  karmicPath: (body: ChartCreateRequest) =>
    request<KarmicPathResponse>('/karmic-path', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  karmicPathSample: () => request<KarmicPathResponse>('/karmic-path/sample'),

  // Karmic Path PDF — same input as karmicPath, returns a Blob.
  karmicPathPdfBlob: async (body: ChartCreateRequest, lang: Lang = 'hi'): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    const res = await rawRequest(`/reports/karmic.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // Health & Vulnerability — concerned, non-alarmist medical-astro report.
  healthReport: (body: ChartCreateRequest, asOf?: string) =>
    request<HealthVulnerabilityResponse>(
      `/health-vulnerability${asOf ? `?as_of=${asOf}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Health Vulnerability PDF — same input as healthReport, returns a Blob.
  healthReportPdfBlob: async (
    body: ChartCreateRequest,
    lang: Lang = 'hi',
    asOf?: string,
  ): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    if (asOf) qs.set('as_of', asOf);
    const res = await rawRequest(`/reports/health.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // Compatibility (Ashtakoot 36-गुण).
  compatibility: (body: CompatibilityRequest) =>
    request<CompatibilityResponse>('/compatibility', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Compatibility PDF — same input as compatibility, returns a Blob.
  compatibilityPdfBlob: async (body: CompatibilityRequest, lang: Lang = 'hi'): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    const res = await rawRequest(`/reports/compatibility.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // Annual Forecast (Varshaphal).
  varshaphal: (body: ChartCreateRequest, forecastYear?: number) =>
    request<VarshaphalResponse>(
      `/varshaphal${forecastYear ? `?forecast_year=${forecastYear}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Vidya Yoga (education report for parents).
  vidyaYoga: (body: ChartCreateRequest) =>
    request<VidyaYogaResponse>('/vidya-yoga', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─── Phase 5.21–5.29 specialty reports ─────────────────────────────────
  bala: (body: ChartCreateRequest) =>
    request<ShadbalaFullResponse>('/bala', {
      method: 'POST', body: JSON.stringify(body),
    }),
  pitraDosha: (body: ChartCreateRequest) =>
    request<PitraDoshaResponse>('/pitra-dosha', {
      method: 'POST', body: JSON.stringify(body),
    }),
  wealthYoga: (body: ChartCreateRequest) =>
    request<WealthYogaResponse>('/wealth-yoga', {
      method: 'POST', body: JSON.stringify(body),
    }),
  manglik: (body: ChartCreateRequest) =>
    request<ManglikResponse>('/manglik', {
      method: 'POST', body: JSON.stringify(body),
    }),
  visaYoga: (body: ChartCreateRequest) =>
    request<VisaYogaResponse>('/visa-yoga', {
      method: 'POST', body: JSON.stringify(body),
    }),

  // Visa Yoga PDF — same input as visaYoga, returns a Blob.
  visaYogaPdfBlob: async (body: ChartCreateRequest, lang: Lang = 'hi'): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    const res = await rawRequest(`/reports/visa.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },
  businessYoga: (body: ChartCreateRequest) =>
    request<BusinessYogaResponse>('/business-yoga', {
      method: 'POST', body: JSON.stringify(body),
    }),
  progenyYoga: (body: ChartCreateRequest) =>
    request<ProgenyYogaResponse>('/progeny-yoga', {
      method: 'POST', body: JSON.stringify(body),
    }),
  legalYoga: (body: ChartCreateRequest) =>
    request<LegalYogaResponse>('/legal-yoga', {
      method: 'POST', body: JSON.stringify(body),
    }),
  sadeSati: (body: ChartCreateRequest, asOf?: string) =>
    request<SadeSatiResponse>(
      `/sade-sati${asOf ? `?as_of=${asOf}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  rectify: (body: RectificationRequest) =>
    request<RectificationResponse>('/rectify', {
      method: 'POST', body: JSON.stringify(body),
    }),
  careerCompatibility: (body: CompatibilityRequest) =>
    request<CareerCompatibilityResponse>('/career-compatibility', {
      method: 'POST', body: JSON.stringify(body),
    }),

  // Career-Compatibility PDF — same input as careerCompatibility.
  careerCompatibilityPdfBlob: async (body: CompatibilityRequest, lang: Lang = 'hi'): Promise<Blob> => {
    const qs = new URLSearchParams({ lang });
    const res = await rawRequest(`/reports/career-compatibility.pdf?${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // GET-by-id PDF — re-renders an already-persisted chart. Optional
  // expert_note adds a "विशेषज्ञ की टिप्पणी / Expert's Personal Note"
  // section at the top of the report.
  careerPdfBlobById: async (
    chartId: number,
    lang: Lang = 'hi',
    expertNote?: string,
  ): Promise<Blob> => {
    const qs = new URLSearchParams({ chart_id: String(chartId), lang });
    if (expertNote && expertNote.trim()) {
      qs.set('expert_note', expertNote.trim());
    }
    const res = await rawRequest(`/reports/career.pdf?${qs}`, {
      method: 'GET',
      headers: { Accept: 'application/pdf' },
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? detail;
      } catch {/* ignore */}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },
};

export { ApiError };
export type { GeocodeOut, LoginOk, NarrationResponse, WhoAmI };
